<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 transition-colors duration-200 relative">
    <!-- Watermark -->
    <div class="fixed inset-0 flex items-center justify-center pointer-events-none z-0 opacity-[0.03] dark:opacity-[0.02]">
      <img src="/assets/images/atqan-logo.svg" alt="Atqan Watermark" class="w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 transform rotate-[-30deg]" />
    </div>

    <div class="relative z-10">
      <AppHeader />

      <!-- App Shell -->
      <div class="flex mt-14 sm:mt-16 min-h-[calc(100vh-3.5rem)] sm:min-h-[calc(100vh-4rem)]">

        <!-- Sidebar -->
        <aside
          :class="[
            'fixed top-14 sm:top-16 h-[calc(100vh-3.5rem)] sm:h-[calc(100vh-4rem)] z-40 flex flex-col',
            isRTL ? 'right-0 border-l' : 'left-0 border-r',
            'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700',
            'transition-all duration-300 ease-in-out',
            sidebarOpen ? 'w-64' : 'w-16',
          ]"
        >
          <!-- App Identity -->
          <div class="flex items-center gap-3 px-3 py-4 border-b border-gray-200 dark:border-gray-700 overflow-hidden min-h-[68px]">
            <div class="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-violet-500/20">
              <UIcon name="i-simple-icons-github" class="w-5 h-5 text-white" />
            </div>
            <Transition name="fade-slide">
              <div v-if="sidebarOpen" class="min-w-0 flex-1">
                <p class="text-sm font-semibold text-gray-900 dark:text-white truncate leading-tight">
                  {{ app?.name || t('sidebar.loading') }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {{ app?.github_repo }}
                </p>
              </div>
            </Transition>
          </div>

          <!-- Deploy Status Strip -->
          <Transition name="fade-slide">
            <div v-if="sidebarOpen && app" class="mx-3 mt-3 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <div class="flex items-center gap-2">
                <span
                  :class="[
                    'w-2 h-2 rounded-full flex-shrink-0',
                    app.deploy_status === 'running' ? 'bg-green-500 animate-pulse' :
                    app.deploy_status === 'deploying' ? 'bg-yellow-500 animate-pulse' :
                    app.deploy_status === 'error' ? 'bg-red-500' : 'bg-gray-400'
                  ]"
                />
                <span class="text-xs font-medium text-gray-600 dark:text-gray-400 capitalize">
                  {{ app.deploy_status || t('sidebar.unknown') }}
                </span>
              </div>
            </div>
          </Transition>

          <!-- Navigation -->
          <nav class="flex-1 overflow-y-auto py-3 space-y-1 px-2">
            <template v-for="section in navSections" :key="section.label">
              <!-- Section Label -->
              <div v-if="sidebarOpen">
                <Transition name="fade-slide">
                  <p class="px-2 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                    {{ section.label }}
                  </p>
                </Transition>
              </div>
              <div v-else class="pt-2 pb-1 flex justify-center">
                <div class="w-6 border-t border-gray-200 dark:border-gray-700" />
              </div>

              <NuxtLink
                v-for="item in section.items"
                :key="item.to"
                :to="item.to"
                class="group flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm font-medium transition-all duration-150 relative"
                :class="[
                  isActive(item.to)
                    ? 'bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-400'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                ]"
                :title="!sidebarOpen ? item.label : undefined"
              >
                <!-- Active indicator -->
                <span
                  v-if="isActive(item.to)"
                  :class="[
                    'absolute top-1/2 -translate-y-1/2 w-0.5 h-5 bg-violet-600 dark:bg-violet-400',
                    isRTL ? 'right-0 rounded-l' : 'left-0 rounded-r',
                  ]"
                />
                <UIcon
                  :name="item.icon"
                  :class="[
                    'w-5 h-5 flex-shrink-0 transition-colors',
                    isActive(item.to) ? 'text-violet-600 dark:text-violet-400' : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300'
                  ]"
                />
                <Transition name="fade-slide">
                  <span v-if="sidebarOpen" class="truncate">{{ item.label }}</span>
                </Transition>
                <Transition name="fade-slide">
                  <UBadge v-if="sidebarOpen && item.badge" :color="item.badgeColor || 'gray'" size="xs" :class="isRTL ? 'mr-auto' : 'ml-auto'">
                    {{ item.badge }}
                  </UBadge>
                </Transition>
              </NuxtLink>
            </template>
          </nav>

          <!-- Bottom Actions -->
          <div class="border-t border-gray-200 dark:border-gray-700 p-2 space-y-1">
            <!-- Back to Apps -->
            <NuxtLink
              to="/apps"
              class="group w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-all duration-150"
              :title="!sidebarOpen ? 'All Apps' : undefined"
            >
              <UIcon :name="isRTL ? 'i-heroicons-arrow-right' : 'i-heroicons-arrow-left'" class="w-5 h-5 flex-shrink-0" />
              <Transition name="fade-slide">
                <span v-if="sidebarOpen" class="text-sm">{{ t('sidebar.allApps') }}</span>
              </Transition>
            </NuxtLink>

            <!-- Deploy Button -->
            <button
              @click="deployApp"
              :disabled="app?.deploy_status !== 'running'"
              class="group w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm font-medium transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
              :class="app?.deploy_status === 'running' ? 'text-violet-700 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-500/10' : 'text-gray-400 dark:text-gray-600'"
              :title="!sidebarOpen ? 'Deploy' : undefined"
            >
              <UIcon name="i-heroicons-rocket-launch" class="w-5 h-5 flex-shrink-0" />
              <Transition name="fade-slide">
                <span v-if="sidebarOpen">{{ t('sidebar.deploy') }}</span>
              </Transition>
            </button>

            <!-- Toggle Button -->
            <button
              @click="sidebarOpen = !sidebarOpen"
              class="w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-150"
              :title="sidebarOpen ? t('sidebar.collapse') : t('sidebar.expand')"
            >
              <UIcon
                :name="isRTL ? (sidebarOpen ? 'i-heroicons-chevron-double-right' : 'i-heroicons-chevron-double-left') : (sidebarOpen ? 'i-heroicons-chevron-double-left' : 'i-heroicons-chevron-double-right')"
                class="w-5 h-5 flex-shrink-0"
              />
              <Transition name="fade-slide">
                <span v-if="sidebarOpen" class="text-xs">{{ t('sidebar.collapse') }}</span>
              </Transition>
            </button>
          </div>
        </aside>

        <!-- Main Content -->
        <main
          :class="[
            'flex-1 transition-all duration-300 ease-in-out',
            isRTL
              ? (sidebarOpen ? 'mr-64' : 'mr-16')
              : (sidebarOpen ? 'ml-64' : 'ml-16'),
            'px-4 sm:px-6 lg:px-8 py-6',
          ]"
        >
          <slot />
        </main>
      </div>

      <UNotifications />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, provide } from 'vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { initializeLanguage, isRTL } = useLanguage();

const appId = computed(() => route.params.id as string);
const sidebarOpen = ref(true);

const app = ref<any>(null);

const fetchApp = async () => {
  if (!appId.value) return;
  try {
    const { authenticatedFetch } = useAuthenticatedFetch();
    app.value = await authenticatedFetch(`/api/v1/apps/${appId.value}`);
  } catch (err) {
    console.error('Failed to fetch app:', err);
  }
};

provide('app', app);
provide('appId', appId);
provide('refreshApp', fetchApp);

const navSections = computed(() => [
  {
    label: t('sidebar.sections.overview'),
    items: [
      { label: t('sidebar.nav.overview'),     icon: 'i-heroicons-home',                      to: `/apps/${appId.value}` },
      { label: t('sidebar.nav.deployments'),  icon: 'i-heroicons-rocket-launch',             to: `/apps/${appId.value}/deployments` },
      { label: t('sidebar.nav.logs'),         icon: 'i-heroicons-document-text',             to: `/apps/${appId.value}/logs` },
    ],
  },
  {
    label: t('sidebar.sections.github'),
    items: [
      { label: t('sidebar.nav.pullRequests'),  icon: 'i-heroicons-arrow-path-rounded-square', to: `/apps/${appId.value}/pull-requests` },
      { label: t('sidebar.nav.repoAnalysis'),  icon: 'i-heroicons-chart-bar',                to: `/apps/${appId.value}/repo-analysis` },
      { label: t('sidebar.nav.folderAnalysis'),icon: 'i-heroicons-folder-open',              to: `/apps/${appId.value}/folder-analysis` },
      { label: t('sidebar.nav.branchPRs'),     icon: 'i-heroicons-code-bracket',             to: `/apps/${appId.value}/branch-prs` },
    ],
  },
  {
    label: t('sidebar.sections.tools'),
    items: [
      { label: t('sidebar.nav.systemHelper'), icon: 'i-heroicons-wrench-screwdriver', to: `/system-helper/${appId.value}` },
    ],
  },
  {
    label: t('sidebar.sections.settings'),
    items: [
      { label: t('sidebar.nav.appManagement'), icon: 'i-heroicons-cog-6-tooth',    to: `/apps/${appId.value}/app-management` },
      { label: t('sidebar.nav.editApp'),       icon: 'i-heroicons-pencil-square',  to: `/apps/${appId.value}/edit` },
    ],
  },
]);

const isActive = (to: string) => {
  if (to === `/apps/${appId.value}`) {
    return route.path === to;
  }
  return route.path === to || route.path.startsWith(to + '/');
};

const deployApp = () => {
  // TODO: implement deploy action
};

const fadeDir = computed(() => isRTL.value ? '6px' : '-6px');

onMounted(async () => {
  initializeLanguage();
  await fetchApp();
  // Auto-collapse on small screens
  if (window.innerWidth < 768) {
    sidebarOpen.value = false;
  }
});
</script>

<style scoped>
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(v-bind(fadeDir));
}
</style>
